import { get, first, includes } from 'lodash';

import store from '../store/index';
import api from '../lib/api';
import detectLanguage from '../lib/detect-language';
import {
  userExists,
  authenticateUserWithPassword,
  guestSignUp,
  userSignUp,
  upgradeGuestUser,
  createUserPaymentSource,
  updateUserLanguage,
} from './users';
import {
  setCurrentMembership,
  fetchPurchaseMemberships
} from './memberships';
import { submitPayment } from './payments';
import {
  hideMobileModal,
  showAlert,
  ALERT_DASHBOARD_MEMBERS_INVITED
} from './ui';
import Purchase from '../lib/purchase';
import { handle401 } from './error-handlers';

export const REQUEST_PURCHASE = 'REQUEST_PURCHASE';
export const FETCH_PURCHASE = 'FETCH_PURCHASE';

export const REQUEST_DUPLICATED_PURCHASE = 'REQUEST_DUPLICATED_PURCHASE';
export const FETCH_DUPLICATED_PURCHASE = 'FETCH_DUPLICATED_PURCHASE';

export const SET_CURRENT_PURCHASE = 'SET_CURRENT_PURCHASE';

export const REQUEST_CLAIM_PURCHASE = 'REQUEST_CLAIM_PURCHASE';
export const FETCH_CLAIM_PURCHASE = 'FETCH_CLAIM_PURCHASE';

export const REQUEST_PURCHASE_PROJECTION = 'REQUEST_PURCHASE_PROJECTION';
export const FETCH_PURCHASE_PROJECTION = 'FETCH_PURCHASE_PROJECTION';

export const REQUEST_INVITE_PEOPLE = 'REQUEST_INVITE_PEOPLE';
export const SEND_INVITE_PEOPLE = 'SEND_INVITE_PEOPLE';

export const REQUEST_CANCEL_PURCHASE = 'REQUEST_CANCEL_PURCHASE';
export const CANCEL_PURCHASE = 'CANCEL_PURCHASE';

export const REQUEST_UPDATE_PURCHASE = 'REQUEST_UPDATE_PURCHASE';
export const UPDATE_PURCHASE = 'UPDATE_PURCHASE';

export const REQUEST_COMMIT_TO_PURCHASE = 'REQUEST_COMMIT_TO_PURCHASE';
export const FETCH_COMMIT_TO_PURCHASE = 'FETCH_COMMIT_TO_PURCHASE';

export const REQUEST_WITHDRAW_FROM_PURCHASE = 'REQUEST_WITHDRAW_FROM_PURCHASE';
export const WITHDRAW_FROM_PURCHASE = 'WITHDRAW_FROM_PURCHASE';

export const REQUEST_ENABLE_AUTO_PILOT = 'REQUEST_ENABLE_AUTO_PILOT';
export const FETCH_ENABLE_AUTO_PILOT = 'FETCH_ENABLE_AUTO_PILOT';

export const REQUEST_DISABLE_AUTO_PILOT = 'REQUEST_DISABLE_AUTO_PILOT';
export const FETCH_DISABLE_AUTO_PILOT = 'FETCH_DISABLE_AUTO_PILOT';

export const REQUEST_PURCHASE_LEGAL_DOCUMENTS = 'REQUEST_PURCHASE_LEGAL_DOCUMENTS';
export const FETCH_PURCHASE_LEGAL_DOCUMENTS = 'FETCH_PURCHASE_LEGAL_DOCUMENTS';

export const REQUEST_CONSUMER_FIELD_SCHEMAS = 'REQUEST_CONSUMER_FIELD_SCHEMAS';
export const FETCH_CONSUMER_FIELD_SCHEMAS = 'FETCH_CONSUMER_FIELD_SCHEMAS';

export const REQUEST_SAVE_CONSUMER_FIELD_VALUES = 'REQUEST_SAVE_CONSUMER_FIELD_VALUES';
export const SAVE_CONSUMER_FIELD_VALUES = 'SAVE_CONSUMER_FIELD_VALUES';

export const REQUEST_PURCHASE_ACTIVITIES = 'REQUEST_PURCHASE_ACTIVITIES';
export const FETCH_PURCHASE_ACTIVITIES = 'FETCH_PURCHASE_ACTIVITIES';

export const REQUEST_POST_PURCHASE_COMMENT = 'REQUEST_POST_PURCHASE_COMMENT';
export const FETCH_POST_PURCHASE_COMMENT = 'FETCH_POST_PURCHASE_COMMENT';

// FIXME: https://www.pivotaltracker.com/story/show/152151273
const fetchLoggedInUser = () => {
  const { users, session: { authentication } } = store.getState();

  return {
    type: 'FETCH_AUTHENTICATED_USER',
    payload: {
      ...users.byId[users.current],
      accessToken: authentication.token,
    },
  };
};

export const fetchPurchase = id => (
  (dispatch) => {
    dispatch({ type: REQUEST_PURCHASE, payload: { id } });
    return api.get(`/purchases/${id}`)
      .then(({ data: purchase }) => {
        dispatch({ type: SET_CURRENT_PURCHASE, payload: purchase });
        const res = dispatch({ type: FETCH_PURCHASE, payload: purchase });
        dispatch(setCurrentMembership());
        return res;
      })
      .catch(err => {
        if (!includes(err.message, 'Nock:')) return Promise.reject(err);
      });
  }
);

export const fetchDuplicatedPurchase = (id, email) => (
  (dispatch) => {
    dispatch({ type: REQUEST_DUPLICATED_PURCHASE, payload: { id, email } });
    return api.get(`/purchases/${id}/duplicated`, { params: { email } })
      .then((response) => {
        return dispatch({ type: FETCH_DUPLICATED_PURCHASE, payload: response.data });
      })
      .catch(() => dispatch({ type: FETCH_DUPLICATED_PURCHASE, payload: { id: null } }));
  }
);

export const projectPurchase = (purchase, namespace) => (
  (dispatch) => {
    const emptyIdsOnMemberships = purchase.memberships && purchase.memberships.some(m => !m.id);
    if (emptyIdsOnMemberships) throw new Error('Passing memberships with no ID is not allowed for projectPurchase');

    const data = {
      ...purchase,
      group: {},
    };
    const purchaseGroup = purchase.group || {};

    if (purchaseGroup.minContribution) {
      data.group.minContribution = purchaseGroup.minContribution;
    }

    if (purchaseGroup.minSlots) {
      data.group.minSlots = purchaseGroup.minSlots;
    }

    if (purchaseGroup.maxSlots) {
      data.group.maxSlots = purchaseGroup.maxSlots;
    }

    if (purchaseGroup.splitType) {
      data.group.splitType = purchaseGroup.splitType;
    }

    dispatch({ type: REQUEST_PURCHASE_PROJECTION, payload: purchase });
    return api.post(`/purchases/${purchase.id}/projections`, data)
      .then((response) => {
        const payload = { id: purchase.id, ...response.data };
        if (namespace) {
          payload.namespace = namespace;
        }

        return dispatch({
          type: FETCH_PURCHASE_PROJECTION,
          payload,
        });
      });
  }
);

export const submitClaimForm = (purchase, data, authentication) => {
  const email = data.organizerEmail;
  const password = data.organizerPassword;
  const fullName = data.organizerName;
  const invitees = data.invitees || [];

  const claimProps = {
    organizerAllowedShareAmount: data.organizerAllowedShareAmount,
    minContribution: data.minContribution,
    invitees: invitees.filter(i => i.email),
    message: data.personalMessage,
    group: {
      splitType: data.splitType,
      minSlots: data.minSlots,
    },
  };

  return (dispatch) => {
    if (authentication.token) {
      return dispatch(claimPurchase(purchase.id, claimProps, authentication.token))
        .catch(handle401(dispatch));
    }

    return dispatch(userExists(email))
      .then(({ payload: exists }) => {
        if (exists) {
          return dispatch(authenticateUserWithPassword(email, password))
            .then(({ payload: user }) => Promise.resolve(user.accessToken))
        }

        const language = detectLanguage() || 'en';

        return dispatch(guestSignUp(email, fullName))
          .then(({ payload: user }) => {
            dispatch(updateUserLanguage(language, user.accessToken));

            return Promise.resolve(user.accessToken)
          });
      }).then(token => dispatch(claimPurchase(purchase.id, claimProps, token)))
  };
};

export const claimPurchase = (purchaseId, data, token) => {
  const headers = {
    authorization: { bearer: token },
  };

  return (dispatch) => {
    dispatch({ type: REQUEST_CLAIM_PURCHASE, payload: data });
    return api.post(`/purchases/${purchaseId}/claim`, data, headers).then(res => (
      dispatch({
        type: FETCH_CLAIM_PURCHASE,
        payload: res.data,
      })
    ))
      .catch(handle401(dispatch));
  };
};

export const fetchConsumerFieldSchemas = (purchaseId, token) => {
  const headers = {};

  if (token) {
    headers.authorization = { bearer: token };
  };

  return (dispatch) => {
    dispatch({ type: REQUEST_CONSUMER_FIELD_SCHEMAS, payload: {} });
    return api.get(`/purchases/${purchaseId}/consumer_field_schemas`, headers)
      .then(res => (
        dispatch({
          type: FETCH_CONSUMER_FIELD_SCHEMAS,
          payload: {
            purchaseSlug: purchaseId,
            data: res.data,
          },
        })
      ))
      .catch(handle401(dispatch));
  };
};

export const savePurchaseConsumerFieldValues = (purchaseId, data, authentication) => {
  const headers = {
    authorization: { bearer: authentication.token },
  };

  return (dispatch) => {
    dispatch({ type: REQUEST_SAVE_CONSUMER_FIELD_VALUES, payload: data });
    return api.post(`/purchases/${purchaseId}/consumer_field_values`, data, headers).then(res => (
      dispatch({
        type: SAVE_CONSUMER_FIELD_VALUES,
        payload: res.data,
      })
    ))
      .catch(handle401(dispatch));
  };
};

export function invitePeople(purchaseId, authentication, { invitees, subject, message }) {
  return (dispatch) => {
    dispatch({ type: REQUEST_INVITE_PEOPLE, payload: { invitees } });
    return api.post(`/purchases/${purchaseId}/invite`, { invitees, subject, message }, {
      authorization: { bearer: authentication.token },
    })
      .then(res => {
        dispatch(fetchPurchase(purchaseId));
        dispatch(fetchPurchaseMemberships(purchaseId, 100));
        dispatch(hideMobileModal())
        dispatch(showAlert(ALERT_DASHBOARD_MEMBERS_INVITED))
        dispatch({ type: SEND_INVITE_PEOPLE, payload: res.data });
      })
      .then(() => dispatch(fetchPurchaseActivities(purchaseId, authentication)))
      .catch(handle401(dispatch));
  };
}

export function cancelPurchase(purchaseId, authentication, props) {
  const headers = {
    authorization: { bearer: get(authentication, 'token') },
  };
  return (dispatch) => {
    dispatch({ type: REQUEST_CANCEL_PURCHASE });
    return api.post(`/purchases/${purchaseId}/cancel`, props, headers)
      .then(() => (
        dispatch({ type: CANCEL_PURCHASE, payload: { id: purchaseId, status: 'canceled' } })
      ))
      .catch(handle401(dispatch));
  };
}

export function updatePurchase(purchaseId, authentication, props) {
  const headers = {
    authorization: { bearer: get(authentication, 'token') },
  };
  return (dispatch) => {
    dispatch({ type: REQUEST_UPDATE_PURCHASE });
    return api.patch(`/purchases/${purchaseId}`, props, headers)
      .then(res => dispatch({ type: UPDATE_PURCHASE, payload: res.data }))
      .catch(handle401(dispatch));
  };
}

export const commitToPurchase = (purchaseId, data, token) => {
  const url = `/purchases/${purchaseId}/commit`;
  return dispatch => dispatch(doCommit(purchaseId, data, token, url))
    .then(() => dispatch(fetchPurchaseActivities(purchaseId, { token })))
    .catch(handle401(dispatch));
};

export const commitWithDeposit = (purchaseId, data, token) => {
  const url = `/purchases/${purchaseId}/payments/deposit`;
  return dispatch => dispatch(doCommit(purchaseId, data, token, url))
    .catch(handle401(dispatch));
};

const doCommit = (purchaseId, data, token, url) => {
  const headers = {
    authorization: { bearer: token },
  };

  return (dispatch) => {
    dispatch({ type: REQUEST_COMMIT_TO_PURCHASE, payload: data });
    return api.post(url, data, headers)
      .then(res => {
        dispatch({
          type: FETCH_COMMIT_TO_PURCHASE,
          payload: res.data
        });
        dispatch(fetchPurchase(purchaseId));
      })
      .catch(handle401(dispatch));
  };
};

export const submitOrgCommitForm = (purchase, data, userAccessToken) => {
  return (dispatch) => {
    return dispatch(upgradeGuestUserIfNeeded(data, userAccessToken))
      .then(() => dispatch(createPaymentSourceIfNeeded(data, userAccessToken)))
      .then((paymentSourceId) => {
        const commitProps = {
          userId: data.user.id,
          optInMarketing: data.optInMarketing,
          consumerFieldValues: data.consumerFieldValues,
          allowedFees: data.allowedFees,
          allowedShareAmount: data.allowedShareAmount,
          paymentSourceId,
        };

        if (!Purchase.isSpecifiedPerPerson(purchase.group.splitType)) {
          commitProps.claimedSlots = data.claimedSlots || 1;
        }

        if (data.reserveDeposit) {
          return dispatch(commitWithDeposit(purchase.id, commitProps, userAccessToken));
        }

        return dispatch(commitToPurchase(purchase.id, commitProps, userAccessToken));
      });
  };
};

export const skipCommit = (data, userAccessToken) => {
  return dispatch => dispatch(upgradeGuestUserIfNeeded(data, userAccessToken));
};

export const submitDashboardCommitForm = (purchase, data, userAccessToken) => {
  let accessToken = userAccessToken;
  let user;
  const language = detectLanguage() || 'en';

  return (dispatch) => {
    return dispatch(signUpAndAuthenticateUserIfNeeded(data, accessToken))
      .then(({ payload }) => {
        user = { ...payload };
        accessToken = user.accessToken;

        dispatch(updateUserLanguage(language, accessToken));
        return dispatch(createPaymentSourceIfNeeded({ ...data, user }, accessToken));
      })
      .then((paymentSourceId) => {
        const commitProps = {
          userId: user.id,
          optInMarketing: data.optInMarketing,
          consumerFieldValues: data.consumerFieldValues,
          allowedFees: data.allowedFees,
          allowedShareAmount: data.allowedShareAmount,
          paymentSourceId,
        };

        if (!Purchase.isSpecifiedPerPerson(purchase.group.splitType)) {
          commitProps.claimedSlots = data.claimedSlots || 1;
        }

        return dispatch(commitToPurchase(purchase.id, commitProps, accessToken));
      });
  };
};

export const submitSinglePayerPayment = (purchase, data, userAccessToken) => {
  let user;
  let accessToken = userAccessToken;
  const language = detectLanguage() || 'en';

  return (dispatch) => {
    return dispatch(signUpAndAuthenticateUserIfNeeded(data, accessToken))
      .then(({ payload }) => {
        user = { ...payload };
        accessToken = user.accessToken;

        dispatch(updateUserLanguage(language, accessToken));
        return dispatch(createPaymentSourceIfNeeded({ ...data, user }, accessToken));
      })
      .then((paymentSourceId) => {
        const commitProps = {
          userId: user.id,
          optInMarketing: data.optInMarketing,
          consumerFieldValues: data.consumerFieldValues,
          allowedFees: data.allowedFees,
          allowedShareAmount: data.allowedShareAmount,
          paymentSourceId,
          claimedSlots: 1,
        };

        return dispatch(commitToPurchase(purchase.id, commitProps, accessToken));
      })
      .then(() => {
        return dispatch(submitPayment(purchase.currentPayment.id, {token: accessToken}, {}));
      });
  };
}

const upgradeGuestUserIfNeeded = (data, accessToken) => {
  const password = get(data, 'auth.password', null);
  const email = get(data, 'auth.email', null);
  return (dispatch) => {
    return dispatch(userExists(email))
      .then(({ payload: exists }) => {
        if (exists === 'guest') {
          return dispatch(upgradeGuestUser(password, accessToken));
        }
      });
  };
};

const signUpAndAuthenticateUserIfNeeded = (data, accessToken) => {
  const password = get(data, 'auth.password', null);
  const email = get(data, 'auth.email', null);
  const fullName = get(data, 'card.fullName', null);
  const createGuestAccount = get(data, 'auth.createGuestAccount', false);

  return (dispatch) => {
    if (!data.createAccount && data.email && data.password) {
      return dispatch(authenticateUserWithPassword(data.email, data.password));
    }

    if (accessToken) {
      return dispatch(() => Promise.resolve(fetchLoggedInUser()));
    }

    return dispatch(userExists(email))
      .then(({ payload: exists }) => {
        if (!exists) {
          if (createGuestAccount) {
            return dispatch(guestSignUp(email, fullName))
              .then(() => fetchLoggedInUser());
          }

          return dispatch(userSignUp(email, password, fullName))
            .then(() => dispatch(authenticateUserWithPassword(email, password)));
        }
        else {
          throw new Error('no_pwd');
        }
      });
  };
};

const createPaymentSourceIfNeeded = ({ card, user }, accessToken) => {
  return (dispatch) => {
    if (card.paymentSourceId && card.paymentSourceId !== 'new') {
      return Promise.resolve(card.paymentSourceId);
    }
    return dispatch(createUserPaymentSource(user, card, accessToken))
      .then(({ payload }) => first(payload.paymentSources).id);
  };
};

export function withdrawFromPurchase(purchaseId, authentication) {
  const headers = {
    authorization: { bearer: authentication.token },
  };

  return (dispatch) => {
    dispatch({ type: REQUEST_WITHDRAW_FROM_PURCHASE });
    return api.post(`/purchases/${purchaseId}/withdraw`, {}, headers)
      .then(res => dispatch(
        {
          type: WITHDRAW_FROM_PURCHASE,
          payload: res.data
        }
      ))
      .then(() => dispatch(fetchPurchase(purchaseId)))
      .then(() => dispatch(fetchPurchaseActivities(purchaseId, authentication)))
      .catch(handle401(dispatch));
  };
}

export function enableAutoPilot(purchaseId, data, authentication) {
  return (dispatch) => {
    dispatch({ type: REQUEST_ENABLE_AUTO_PILOT });
    const params = { triggerSlots: data.autoPilotTriggerSlots };
    return api.post(`/purchases/${purchaseId}/enable_auto_pilot`, params, {
      authorization: { bearer: authentication.token },
    }).then(() => dispatch({
      type: FETCH_ENABLE_AUTO_PILOT,
      payload: {
        id: purchaseId,
        autoPilotTriggerSlots: data.autoPilotTriggerSlots,
        autoPilotEnabled: true,
      },
    }))

  };
}

export function disableAutoPilot(purchaseId, authentication) {
  const headers = {
    authorization: { bearer: get(authentication, 'token') },
  };
  return (dispatch) => {
    dispatch({ type: REQUEST_DISABLE_AUTO_PILOT });
    return api.post(`/purchases/${purchaseId}/disable_auto_pilot`, {}, headers)
      .then(() => dispatch({
        type: FETCH_DISABLE_AUTO_PILOT,
        payload: {
          id: purchaseId,
          autoPilotTriggerSlots: undefined,
          autoPilotEnabled: false,
        },
      }))
      .catch(handle401(dispatch));
  };
}

export function fetchPurchaseLegalDocuments(purchaseId) {
  return (dispatch) => {
    dispatch({ type: REQUEST_PURCHASE_LEGAL_DOCUMENTS });
    return api.get(`/purchases/${purchaseId}/legal_documents`)
      .then(res => dispatch({ type: FETCH_PURCHASE_LEGAL_DOCUMENTS, payload: res.data }));
  };
}

export const fetchPurchaseActivities = (purchaseId, auth) => {
  const headers = {
    authorization: { bearer: get(auth, 'token') },
  };

  return (dispatch) => {
    dispatch({ type: REQUEST_PURCHASE_ACTIVITIES });

    return api.get(`/purchases/${purchaseId}/activities`, headers)
      .then((res) => {
        return dispatch({ type: FETCH_PURCHASE_ACTIVITIES, payload: { purchaseId, activities: res.data } });
      });
  }
}

export const postPurchaseComment = (purchaseId, auth, data) => {
  const headers = {
    authorization: { bearer: get(auth, 'token') },
  }

  return (dispatch) => {
    dispatch({ type: REQUEST_POST_PURCHASE_COMMENT });

    return api.post(`/purchases/${purchaseId}/comments`, data, headers)
      .then((res) => {

        return api.get(`/purchases/${purchaseId}/activities`, headers)
          .then((res) => {
            return dispatch({ type: FETCH_PURCHASE_ACTIVITIES, payload: { purchaseId, activities: res.data } });
          });
      });
  }
}



// WEBPACK FOOTER //
// ./src/actions/purchases.js